import { css } from 'styled-components';

/**
 * IoT 모니터링(/iot_devices)·IR 제어 로그(/iot_control_logs) 전용 디자인 토큰.
 *
 * 값은 디자인 리뉴얼 시안(02 목록·04 IR 로그)의 인라인 스타일과 CHIP·STATUS·ROLE 정의에서 옮겼다.
 * 클래식·플랫 관리자 테마와 팔레트가 달라 두 화면만 이 모듈을 쓴다. 색 리터럴은 이 파일에서만
 * 정의하고 페이지 파일은 토큰만 참조한다(admin_classic_theme.js와 같은 규범). 센서 구분색은
 * iot_metrics.js의 METRIC_COLORS가 정본이라 여기서 다시 정의하지 않는다.
 */
export const iotColor = Object.freeze({
    page: '#eef1f6',
    surface: '#ffffff',
    ink: '#0f1a2b',
    text: '#334155',
    textSub: '#475569',
    textMuted: '#64748b',
    cardLine: '#e6eaf1',
    cardHeaderLine: '#eef1f6',
    controlLine: '#d8dee8',
    rowLine: '#f1f4f9',
    rowHover: '#f7f9fc',
    headerRow: '#f7f9fc',
    disabledRow: '#fcfdfe',
    accent: '#007bff',
    accentHover: '#0056b3',
    onDark: '#ffffff',
    chipActive: '#0f1a2b',
    // 시안은 활성 칩 개수도 #64748b였으나 #0f1a2b 위 대비가 3.7:1이라 AA(4.5:1)를 넘는 값으로 둔다.
    chipActiveCount: '#94a3b8',
    errorText: '#b32424',
    success: '#1e8e3e',
    danger: '#d32f2f',
    // 상세 화면 — 사용 중지 버튼(연한 빨강)과 센서 증감(감소 초록 · 증가 주황, 시안 03 그대로 — 좋고 나쁨 판정 아님).
    dangerSoftBg: '#fdf4f4',
    dangerSoftLine: '#f0c9c9',
    dangerSoftText: '#c02626',
    deltaDown: '#1e8e3e',
    deltaUp: '#b26a00'
});

// 상태 톤 — dot(점), bg·border·fg(칩), ring(MQTT 알약 도트 링).
const TONE_ONLINE = Object.freeze({ dot: '#1e8e3e', bg: '#f1f9f3', border: '#cbe3cf', fg: '#1e6e34', ring: 'rgba(30, 142, 62, 0.18)' });
const TONE_OFFLINE = Object.freeze({ dot: '#d32f2f', bg: '#fdf4f4', border: '#f0cccc', fg: '#b32424', ring: 'rgba(211, 47, 47, 0.18)' });
const TONE_WAITING = Object.freeze({ dot: '#007bff', bg: '#f2f7ff', border: '#cfe0fb', fg: '#0b5cad', ring: 'rgba(0, 123, 255, 0.18)' });
const TONE_DISABLED = Object.freeze({ dot: '#64748b', bg: '#f5f7fa', border: '#e2e8f0', fg: '#64748b', ring: 'rgba(100, 116, 139, 0.18)' });

export const CONNECTION_TONES = Object.freeze({
    온라인: TONE_ONLINE,
    오프라인: TONE_OFFLINE,
    '연결 대기': TONE_WAITING,
    사용중지: TONE_DISABLED
});

export const MQTT_TONES = Object.freeze({
    RUNNING: TONE_ONLINE,
    CHECKING: TONE_DISABLED,
    PROBLEM: TONE_OFFLINE
});

export const COMMAND_STATUS_TONES = Object.freeze({
    SUCCEEDED: TONE_ONLINE,
    PUBLISHED: TONE_WAITING,
    PENDING: TONE_WAITING,
    FAILED: TONE_OFFLINE,
    TIMED_OUT: TONE_OFFLINE,
    REJECTED: TONE_OFFLINE
});

const REQUESTER_BADGES = Object.freeze({
    admin: Object.freeze({ bg: '#e8f0fe', fg: '#0b5cad', short: 'A' }),
    staff: Object.freeze({ bg: '#f1f4f9', fg: '#475569', short: 'S' }),
    other: Object.freeze({ bg: '#f5f3ff', fg: '#6d43c8', short: 'U' })
});

export function requesterBadge(role) {
    const key = String(role || '').toLowerCase();
    return REQUESTER_BADGES[key] || REQUESTER_BADGES.other;
}

export const iotRadius = Object.freeze({
    card: '14px',
    control: '9px',
    tag: '7px',
    badge: '5px',
    pill: '999px'
});

export const iotLayout = Object.freeze({
    maxWidth: '1360px',
    pagePadding: '28px 40px 48px',
    pagePaddingSmall: '20px 12px 40px',
    gap: '14px'
});

export const iotFont = Object.freeze({
    mono: css`
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    `
});

export const iotMixin = Object.freeze({
    card: css`
        background: ${iotColor.surface};
        border: 1px solid ${iotColor.cardLine};
        border-radius: ${iotRadius.card};
        box-shadow: 0 1px 2px rgba(15, 26, 43, 0.04);
    `,
    popover: css`
        background: ${iotColor.surface};
        border: 1px solid ${iotColor.cardLine};
        border-radius: ${iotRadius.card};
        box-shadow: 0 10px 32px rgba(15, 26, 43, 0.1);
    `,
    focusRing: css`
        &:focus-visible {
            outline: 2px solid ${iotColor.accent};
            outline-offset: 2px;
        }
    `,
    // 38px 높이 컨트롤(입력·select·보조 버튼) 공통.
    control: css`
        height: 38px;
        border: 1px solid ${iotColor.controlLine};
        border-radius: ${iotRadius.control};
        background: ${iotColor.surface};
        color: ${iotColor.text};
        font-family: inherit;
        font-size: 12.5px;
    `
});
